import React, { FC } from 'react'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import { JobHistoryEntry } from '../../types'
import SectionWrapper from './LockedSectionWrapper'

// Keyed by worker.JobType (api/pkg/worker/types.go). "verify" is the name
// publish jobs were recorded under before the lifecycle rename; it is kept so
// an older entry still renders a word rather than a raw type string.
const JOB_TYPE_LABELS: Record<string, string> = {
  diarize: 'Diarize',
  translate: 'Translate',
  publish: 'Publish',
  reverify: 'Re-verify',
  verify: 'Publish',
}

const formatDuration = (startedAt: string, completedAt?: string): string => {
  if (!completedAt) return '—'
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
  if (ms < 0) return '—'
  const totalSec = Math.round(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  if (m === 0) return `${s}s`
  return `${m}m ${s}s`
}

interface JobHistorySectionProps {
  jobHistory: JobHistoryEntry[]
}

const JobHistorySection: FC<JobHistorySectionProps> = ({ jobHistory }) => {
  const sorted = [...jobHistory].sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  )

  return (
    <SectionWrapper title="Job History" status="complete">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Detail</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((job) => (
              <TableRow key={job.id}>
                <TableCell>{JOB_TYPE_LABELS[job.type] || job.type}</TableCell>
                <TableCell>{job.detail || '—'}</TableCell>
                <TableCell>{new Date(job.started_at).toLocaleString()}</TableCell>
                <TableCell>{formatDuration(job.started_at, job.completed_at)}</TableCell>
                <TableCell>
                  <Chip
                    label={job.status}
                    size="small"
                    color={
                      job.status === 'completed' ? 'success' :
                      job.status === 'failed' ? 'error' :
                      'info'
                    }
                  />
                  {job.error && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {job.error}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </SectionWrapper>
  )
}

export default JobHistorySection
